import { useMemo, useState } from 'react';
import { Table, Typography, Space, Select, Button, Tag, Tooltip, Alert, App } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSessionStore } from '../../stores/sessionStore';
import { useAudits, ACTION_LABEL, type AuditEntry, type AuditQuery } from '../../api/audits';
import { useAssignableUsers } from '../../api/members';
import { useContactGroups } from '../../api/contact-groups';
import { useContacts } from '../../api/contacts';
import { useBomsV2 } from '../../api/boms-v2';
import { useEcns } from '../../api/ecns';
import { NoTenant } from '../../components/NoTenant';
import { fmtTime } from '../../lib/format';
import { exportRowsToXlsx } from '../../lib/excel';
import { LIST_PAGINATION, LIST_SCROLL_Y } from '../../lib/tablePagination';

/** 对象类型 → 中文 */
const ENTITY_TYPE_LABEL: Record<string, string> = {
  bom: 'BOM',
  ecn: 'ECN',
  member: '成员',
  contact: '联系人',
  contact_group: '联系人分组',
  group_contact: '分组联系人',
  email_config: '发件配置',
  tenant: '产品',
  user: '用户',
};

function shortId(id?: string): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function fmtEcnDate(d?: string): string {
  if (!d) return '';
  return d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d;
}

type Filter =
  | { kind: 'all' }
  | { kind: 'user'; id: string; label: string }
  | { kind: 'entity'; id: string; label: string };

export function AuditPage() {
  const { message } = App.useApp();
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const currentTenant = useSessionStore((s) => s.currentTenant);
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });

  const query: AuditQuery = useMemo(() => {
    if (filter.kind === 'user') return { user_id: filter.id, limit: 200 };
    if (filter.kind === 'entity') return { entity_id: filter.id, limit: 200 };
    return { limit: 200 };
  }, [filter]);

  const audits = useAudits(tid, query, !!tid);
  const usersQ = useAssignableUsers(tid);
  const groupsQ = useContactGroups();
  const contactsQ = useContacts(tid);
  const bomsQ = useBomsV2(tid);
  const ecnsQ = useEcns(tid);

  const userMap = useMemo(() => {
    const m: Record<string, { name: string; email: string }> = {};
    for (const u of usersQ.data ?? []) m[u.id] = { name: u.name || '', email: u.email };
    return m;
  }, [usersQ.data]);
  const groupMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of groupsQ.data ?? []) m[g.id] = g.name;
    return m;
  }, [groupsQ.data]);
  const contactMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of contactsQ.data ?? []) m[c.id] = c.name ? `${c.name}（${c.email}）` : c.email;
    return m;
  }, [contactsQ.data]);
  const bomMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of bomsQ.data ?? []) m[b.id] = b.name;
    return m;
  }, [bomsQ.data]);
  const ecnMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of ecnsQ.data ?? [])
      m[e.id] = `${e.affected_pcba || '变更'}（${fmtEcnDate(e.ecn_date)}）`;
    return m;
  }, [ecnsQ.data]);

  if (!tid) return <NoTenant />;

  const userLabel = (uid?: string): string => {
    if (!uid) return '—';
    const u = userMap[uid];
    if (!u) return shortId(uid);
    return u.name ? `${u.name}（${u.email}）` : u.email;
  };

  // 从审计自带的 before/after 快照提取可读名称（对已删除对象仍有效，且是操作当时的真实值）
  const labelFromSnapshot = (r: AuditEntry): string | null => {
    const snap = (r.after ?? r.before) as Record<string, unknown> | undefined;
    if (!snap) return null;
    const str = (v: unknown) => (v == null || v === '' ? '' : String(v));
    switch (r.entity_type) {
      case 'member':
      case 'user':
      case 'contact':
      case 'group_contact': {
        const email = str(snap['email']);
        const name = str(snap['name']);
        if (name && email) return `${name}（${email}）`;
        return email || name || null;
      }
      case 'contact_group': {
        const n = str(snap['name']) || str(snap['group_name']);
        return n || null;
      }
      case 'bom':
        return str(snap['name']) || null;
      case 'ecn': {
        const p = str(snap['affected_pcba']);
        const d = str(snap['ecn_date']);
        if (p || d) return `${p || '变更'}（${fmtEcnDate(d)}）`;
        return null;
      }
      case 'tenant':
        return str(snap['name']) || str(snap['code']) || null;
      default:
        return null;
    }
  };

  // 从当前实体列表解析（实体仍存在时）
  const labelFromLiveList = (r: AuditEntry): string | null => {
    switch (r.entity_type) {
      case 'member':
      case 'user': {
        const u = userMap[r.entity_id];
        return u ? (u.name ? `${u.name}（${u.email}）` : u.email) : null;
      }
      case 'contact':
        return contactMap[r.entity_id] ?? null;
      case 'contact_group':
        return groupMap[r.entity_id] ?? null;
      case 'group_contact': {
        const gid = r.entity_id.split('#')[0];
        const g = gid ? groupMap[gid] : undefined;
        return g ? `${g} / 联系人` : null;
      }
      case 'bom':
        return bomMap[r.entity_id] ?? null;
      case 'ecn':
        return ecnMap[r.entity_id] ?? null;
      default:
        return null;
    }
  };

  // 对象名解析：发件配置/产品优先按当前产品；再用快照（对已删对象有效）；再用当前列表；最后回原始 ID
  const entityText = (r: AuditEntry): string => {
    if (r.entity_type === 'email_config') {
      return currentTenant?.name ? `${currentTenant.name} 发件配置` : '发件配置';
    }
    if (r.entity_type === 'tenant' && currentTenant?.id === r.entity_id) {
      return currentTenant.name;
    }
    return labelFromSnapshot(r) ?? labelFromLiveList(r) ?? r.entity_id;
  };

  const onExport = () => {
    const list = audits.data ?? [];
    if (list.length === 0) {
      message.warning('当前没有可导出的日志');
      return;
    }
    exportRowsToXlsx(
      `操作日志_${currentTenant?.code || tid}_${new Date().toISOString().slice(0, 10)}`,
      ['时间', '操作', '操作人', '平台操作', '对象类型', '对象', 'IP', '变更前', '变更后'],
      list.map((r) => [
        fmtTime(r.created_at),
        ACTION_LABEL[r.action] ?? r.action,
        userLabel(r.user_id),
        r.is_platform_action ? '是' : '',
        ENTITY_TYPE_LABEL[r.entity_type] ?? r.entity_type,
        entityText(r), // 与网站「对象」列一致的可读名称
        r.ip ?? '',
        r.before ? JSON.stringify(r.before) : '',
        r.after ? JSON.stringify(r.after) : '',
      ]),
    );
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        操作日志
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        message="默认展示本产品全部操作时间线"
        description="记录 BOM/ECN、成员、联系人、发件配置、产品等的增删改。「操作人」为执行该操作的登录账号。可用下方「按操作人筛选」，或直接点击表格中的「操作人」「对象」快速筛选，无需输入 ID。"
      />
      <Space wrap>
        <Button
          type={filter.kind === 'all' ? 'primary' : 'default'}
          onClick={() => setFilter({ kind: 'all' })}
        >
          全部时间线
        </Button>
        <Select
          showSearch
          allowClear
          style={{ width: 280 }}
          placeholder="按操作人筛选（可搜索姓名/邮箱）"
          optionFilterProp="label"
          loading={usersQ.isLoading}
          value={filter.kind === 'user' ? filter.id : undefined}
          onChange={(id?: string) =>
            id ? setFilter({ kind: 'user', id, label: userLabel(id) }) : setFilter({ kind: 'all' })
          }
          options={(usersQ.data ?? []).map((u) => ({
            value: u.id,
            label: u.name ? `${u.name}（${u.email}）` : u.email,
          }))}
        />
        {filter.kind === 'entity' && (
          <Tag closable onClose={() => setFilter({ kind: 'all' })} color="blue">
            对象：{filter.label}
          </Tag>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={onExport}
          disabled={(audits.data?.length ?? 0) === 0}
        >
          导出日志
        </Button>
      </Space>

      <Table<AuditEntry>
        rowKey={(r) => `${r.created_at}-${r.entity_id}-${r.action}`}
        loading={audits.isFetching}
        dataSource={audits.data ?? []}
        size="small"
        scroll={{ y: LIST_SCROLL_Y }}
        pagination={LIST_PAGINATION}
        locale={{ emptyText: '无记录' }}
        expandable={{
          expandedRowRender: (r) => (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {r.before && (
                <div>
                  <Typography.Text type="secondary">变更前：</Typography.Text>
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(r.before, null, 2)}
                  </pre>
                </div>
              )}
              {r.after && (
                <div>
                  <Typography.Text type="secondary">变更后：</Typography.Text>
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(r.after, null, 2)}
                  </pre>
                </div>
              )}
              {!r.before && !r.after && (
                <Typography.Text type="secondary">（无前后值快照）</Typography.Text>
              )}
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12 }}
                copyable={{ text: r.entity_id }}
              >
                对象 ID：{r.entity_id}
                {r.user_agent ? ` ｜ UA: ${r.user_agent}` : ''}
              </Typography.Text>
            </Space>
          ),
        }}
        columns={[
          { title: '时间', dataIndex: 'created_at', width: 175, render: fmtTime },
          {
            title: '操作',
            dataIndex: 'action',
            width: 130,
            render: (a: string) => <Tag>{ACTION_LABEL[a] ?? a}</Tag>,
          },
          {
            title: '操作人',
            dataIndex: 'user_id',
            width: 220,
            ellipsis: true,
            render: (u?: string, r?: AuditEntry) => (
              <span>
                {u ? (
                  <Tooltip title="点击按此操作人筛选">
                    <Typography.Link
                      onClick={() => setFilter({ kind: 'user', id: u, label: userLabel(u) })}
                    >
                      {userLabel(u)}
                    </Typography.Link>
                  </Tooltip>
                ) : (
                  '—'
                )}
                {r?.is_platform_action && (
                  <Tag color="gold" style={{ marginInlineStart: 4 }}>
                    平台
                  </Tag>
                )}
              </span>
            ),
          },
          {
            title: '对象类型',
            dataIndex: 'entity_type',
            width: 110,
            render: (t: string) => ENTITY_TYPE_LABEL[t] ?? t,
          },
          {
            title: '对象',
            key: 'entity_id',
            ellipsis: true,
            render: (_: unknown, r) => {
              const txt = entityText(r);
              const resolved = txt !== r.entity_id;
              return (
                <Tooltip title={resolved ? '点击按此对象筛选' : r.entity_id}>
                  <Typography.Link
                    onClick={() => setFilter({ kind: 'entity', id: r.entity_id, label: txt })}
                  >
                    {resolved ? txt : shortId(r.entity_id)}
                  </Typography.Link>
                </Tooltip>
              );
            },
          },
          { title: 'IP', dataIndex: 'ip', width: 130, render: (v?: string) => v ?? '—' },
        ]}
      />
    </Space>
  );
}
